import { type ReactNode, useState } from 'react';
import styles from './DelayUntilEvent.module.css';

type Direction = 'forward' | 'back' | 'none';

/**
 * Slides a panel screen in when the author moves between the Delay panel's screens: from the right
 * when going on to a deeper screen, from the left when going back. `depth` is how many steps the
 * screen is from the Delay form. Only the screen that arrives moves, and the first screen shown
 * does not, so opening the panel is not animated.
 *
 * Keep one in the same place in every screen the panel can show: its memory of the last depth is
 * what tells forward from back.
 */
export function ScreenSlide({
  depth,
  children,
}: {
  depth: number;
  children: ReactNode;
}) {
  const [slide, setSlide] = useState<{
    depth: number;
    direction: Direction;
    run: number;
  }>({ depth, direction: 'none', run: 0 });
  // Worked out during the render that changes the depth, and then kept: a render that changes
  // nothing (typing in a field) must not take the class off an animation that is still running.
  if (slide.depth !== depth) {
    setSlide({
      depth,
      direction: depth > slide.depth ? 'forward' : 'back',
      run: slide.run + 1,
    });
  }
  const directionClass =
    slide.direction === 'forward'
      ? styles.slideForward
      : slide.direction === 'back'
        ? styles.slideBack
        : undefined;
  return (
    <div className={styles.slideViewport}>
      {/* A new key restarts the animation for each screen change. */}
      <div
        key={slide.run}
        className={directionClass}
        data-screen-slide={slide.direction}
      >
        {children}
      </div>
    </div>
  );
}
